import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { reportClientError } from "../lib/errorlog";

/**
 * Wenn eine Seite beim Zeichnen stolpert.
 *
 * Vorher hing genau eine Auffangstelle ganz aussen um die Anwendung. Ein
 * Fehler in irgendeiner Seite ersetzte damit *alles* — Leiste weg,
 * Navigation weg, und der einzige Ausweg war der Zurück-Knopf des Browsers,
 * der nicht einmal half: der Zustand blieb stehen, weil die Auffangstelle
 * nichts von der neuen Adresse wusste.
 *
 * Diese hier sitzt innerhalb des Rahmens. Die Leiste bleibt stehen, man
 * klickt einfach woanders hin — und weil der Rahmen bei jedem Wechsel neu
 * aufgebaut wird (`key={pathname}`), ist der Fehler damit auch vergessen.
 *
 * Die Auffangstelle ganz aussen bleibt trotzdem: stolpert der Rahmen selbst,
 * kann sie niemand von innen abfangen.
 */
export class SeitenFehler extends Component<
  { children: ReactNode },
  { fehler: Error | null }
> {
  state: { fehler: Error | null } = { fehler: null };

  static getDerivedStateFromError(fehler: Error) {
    return { fehler };
  }

  componentDidCatch(fehler: Error, info: ErrorInfo) {
    reportClientError(
      fehler.message || "Render error",
      location.pathname,
      `${fehler.stack ?? ""}\n${info.componentStack ?? ""}`,
    );
  }

  render() {
    if (!this.state.fehler) return this.props.children;
    return (
      <div className="seitenfehler">
        <AlertTriangle className="icon-svg seitenfehler-symbol" />
        <h2>This page stumbled</h2>
        <p className="muted">
          Everything else still works — pick another page on the left, or try
          this one again. The details went to the log.
        </p>
        <button
          type="button"
          className="btn-primary"
          onClick={() => this.setState({ fehler: null })}
        >
          <RotateCcw className="icon-svg" /> Try again
        </button>
      </div>
    );
  }
}
